import type { Asset } from '../store/seedanceTypes';
import { VP_ASSET_TYPE_AUDIO, VP_ASSET_TYPE_VIDEO } from './seedanceConstants';

/** 兼容虚拟人像接口常见的列表响应包装结构。 */
export const normalizeVpApiList = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (data.data && data.data.Result && Array.isArray(data.data.Result.Items)) return data.data.Result.Items;
  if (data.Result && Array.isArray(data.Result.Items)) return data.Result.Items;
  if (data.data && Array.isArray(data.data.Items)) return data.data.Items;
  if (data.data && Array.isArray(data.data.items)) return data.data.items;
  if (Array.isArray(data.Items)) return data.Items;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.result)) return data.result;
  return [];
};

/** 从一组候选值里取第一个非空字符串。 */
export const firstNonEmptyString = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

/** 兼容 url 字段为字符串或 `{ url: "..." }` 包装。 */
export const pickNestedUrlString = (value: unknown): string => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (!value || typeof value !== 'object') return '';
  const o = value as Record<string, unknown>;
  return firstNonEmptyString(o.url, o.URL, o.uri, o.Uri, o.href, o.Href);
};

/** 列表项可能嵌套在 data / portrait / asset 等字段内，先摊平再取 URL。 */
export const unwrapPortraitListItem = (item: any): any => {
  if (!item || typeof item !== 'object') return item;
  
  let target = item;
  if (target.Result && typeof target.Result === 'object') {
    target = target.Result;
  }

  const nested =
    (target.data && typeof target.data === 'object' && !Array.isArray(target.data) ? target.data : null) ??
    (target.portrait && typeof target.portrait === 'object' ? target.portrait : null) ??
    (target.Portrait && typeof target.Portrait === 'object' ? target.Portrait : null) ??
    (target.asset && typeof target.asset === 'object' ? target.asset : null) ??
    (target.Asset && typeof target.Asset === 'object' ? target.Asset : null);
    
  if (!nested) return { ...target, ...item };
  return { ...nested, ...target, ...item };
};

/** 与多数后端一致：字段名为 URL（全大写）或 Url；与 p.url 不同，须单独取。 */
export const pickStringKeyIgnoreCase = (obj: any, nameLower: string): string => {
  if (!obj || typeof obj !== 'object') return '';
  for (const k of Object.keys(obj as object)) {
    if (k.toLowerCase() === nameLower) {
      const v = (obj as Record<string, unknown>)[k];
      const nested = pickNestedUrlString(v);
      if (nested) return nested;
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return '';
};

/**
 * 将虚拟人像接口返回的地址转为 img 可用的绝对地址。
 * 相对路径走当前站 + REACT_APP_BASE_URL 代理；file:// 或单独打包时回退 ai.k23.cn。
 */
export const resolvePortraitImageUrl = (value: string | null | undefined): string | null => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  const apiPublic = (process.env.REACT_APP_API_PUBLIC_ORIGIN || '').replace(/\/$/, '');
  if (apiPublic) return `${apiPublic}${path}`;
  if (typeof window !== 'undefined' && window.location?.protocol && window.location.protocol !== 'file:') {
    const base = (process.env.REACT_APP_BASE_URL || '/proxy/base').replace(/\/$/, '');
    return `${window.location.origin}${base}${path}`;
  }
  return `https://ai.k23.cn${path}`;
};

/** 从虚拟人像素材响应中提取素材名称（兼容大小写字段与 `{ data: {...} }` 包装）。 */
export const pickPortraitName = (p: any): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickStringKeyIgnoreCase(p, 'name'),
    p?.name,
    p?.Name,
    pickStringKeyIgnoreCase(nested, 'name'),
    nested?.name,
    nested?.Name,
  );
};

/** 从虚拟人像素材响应中提取主图/主文件地址。 */
export const pickPortraitMainUrl = (p: any): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickNestedUrlString(pickStringKeyIgnoreCase(p, 'url')),
    pickNestedUrlString(p?.url),
    pickNestedUrlString(p?.URL),
    pickNestedUrlString(p?.uri),
    p?.uri,
    p?.image_url,
    p?.ImageUrl,
    p?.file_url,
    p?.FileUrl,
    p?.imageUrl,
    p?.object_url,
    p?.ObjectUrl,
    p?.result_url,
    p?.ResultUrl,
    pickNestedUrlString(pickStringKeyIgnoreCase(nested, 'url')),
    pickNestedUrlString(nested?.url),
    nested?.image_url,
    nested?.file_url,
  );
};

/** 从虚拟人像素材响应中提取缩略图地址（不含「回退主图」— 避免把视频直链当图片用）。 */
export const pickPortraitThumbOnly = (p: any): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickStringKeyIgnoreCase(p, 'thumbnail_url'),
    pickStringKeyIgnoreCase(p, 'thumb'),
    pickStringKeyIgnoreCase(p, 'cover_url'),
    pickStringKeyIgnoreCase(p, 'poster_url'),
    pickStringKeyIgnoreCase(p, 'preview_image_url'),
    p?.thumbnail_url,
    p?.ThumbnailUrl,
    p?.thumb,
    p?.Thumb,
    p?.cover_url,
    p?.poster_url,
    p?.preview_url,
    p?.PreviewUrl,
    p?.small_url,
    p?.SmallUrl,
    nested?.thumbnail_url,
    nested?.thumb,
    nested?.cover_url,
    nested?.poster_url,
  );
};

/** 视频素材主地址（列表项可能放在 video_url 等与图片不同的字段）。 */
export const pickPortraitVideoUrl = (p: any): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickStringKeyIgnoreCase(p, 'video_url'),
    p?.video_url,
    p?.VideoUrl,
    p?.videoUrl,
    pickStringKeyIgnoreCase(p, 'media_url'),
    pickStringKeyIgnoreCase(p, 'play_url'),
    pickStringKeyIgnoreCase(p, 'source_url'),
    pickStringKeyIgnoreCase(nested, 'video_url'),
    nested?.video_url,
    nested?.VideoUrl,
    nested?.url,
  );
};

/** 音频素材主地址（列表项可能放在 audio_url 等字段）。 */
export const pickPortraitAudioUrl = (p: any): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickNestedUrlString(pickStringKeyIgnoreCase(p, 'audio_url')),
    pickNestedUrlString(p?.audio_url),
    p?.AudioUrl,
    p?.audioUrl,
    pickStringKeyIgnoreCase(p, 'media_url'),
    pickStringKeyIgnoreCase(p, 'play_url'),
    pickStringKeyIgnoreCase(p, 'source_url'),
    pickNestedUrlString(pickStringKeyIgnoreCase(nested, 'audio_url')),
    pickNestedUrlString(nested?.audio_url),
    nested?.AudioUrl,
    nested?.url,
  );
};

/** 按素材类型提取可播放/可预览的主文件地址。 */
export const pickPortraitPlayableUrl = (
  p: any,
  type: 'image' | 'video' | 'audio',
): string => {
  const item = unwrapPortraitListItem(p);
  if (type === 'video') {
    return firstNonEmptyString(pickPortraitVideoUrl(item), pickPortraitMainUrl(item));
  }
  if (type === 'audio') {
    return firstNonEmptyString(
      pickPortraitAudioUrl(item),
      pickPortraitMainUrl(item),
      pickPortraitVideoUrl(item),
    );
  }
  return firstNonEmptyString(pickPortraitMainUrl(item), pickPortraitVideoUrl(item));
};

const looksLikeVideoFileUrl = (u: string): boolean =>
  /\.(mp4|webm|mov|m4v|mkv|avi)(\?|#|$)/i.test((u || '').trim());

const normalizePortraitAssetType = (raw: unknown): 'image' | 'video' | 'audio' => {
  const assetType = String(raw ?? '').trim();
  const lower = assetType.toLowerCase();
  if (lower === 'video' || assetType === VP_ASSET_TYPE_VIDEO) return 'video';
  if (lower === 'audio' || assetType === VP_ASSET_TYPE_AUDIO) return 'audio';
  return 'image';
};

/** 从虚拟人像素材响应中提取缩略图地址，缺失时回退主图。 */
export const pickPortraitThumbUrl = (p: any, mainUrl: string): string => {
  const nested = p?.data && typeof p.data === 'object' ? p.data : null;
  return firstNonEmptyString(
    pickStringKeyIgnoreCase(p, 'thumbnail_url'),
    pickStringKeyIgnoreCase(p, 'thumb'),
    pickStringKeyIgnoreCase(p, 'cover_url'),
    pickStringKeyIgnoreCase(p, 'poster_url'),
    pickStringKeyIgnoreCase(p, 'preview_image_url'),
    p?.thumbnail_url,
    p?.ThumbnailUrl,
    p?.thumb,
    p?.Thumb,
    p?.cover_url,
    p?.poster_url,
    p?.preview_url,
    p?.PreviewUrl,
    p?.small_url,
    p?.SmallUrl,
    nested?.thumbnail_url,
    nested?.thumb,
    nested?.cover_url,
    nested?.poster_url,
    mainUrl,
  );
};

/** 素材名称是否包含搜索词（子串匹配，与 ListAssets Filter.Name 语义一致）。 */
export const assetNameMatchesSearch = (name: string, query: string): boolean => {
  const q = query.trim();
  if (!q) return true;
  return (name || '').includes(q);
};

/** 将虚拟人像「列表接口」整条响应转成素材卡片模型（与工作台 IndexedDB 缓存结构一致）。 */
export const mapVirtualPortraitApiListToAssets = (data: unknown): Asset[] => {
  const items = normalizeVpApiList(data);
  return items.map((rawItem: any) => {
    const item = unwrapPortraitListItem(rawItem);
    const mediaType = normalizePortraitAssetType(item.asset_type ?? item.AssetType);
    const isVideo = mediaType === 'video';
    const playableRaw = pickPortraitPlayableUrl(item, mediaType);
    const imageMain = pickPortraitMainUrl(item);

    const thumbOnlyRaw = pickPortraitThumbOnly(item);
    let thumbResolved: string | null = thumbOnlyRaw
      ? resolvePortraitImageUrl(thumbOnlyRaw)
      : null;
    const urlResolved = playableRaw ? resolvePortraitImageUrl(playableRaw) : null;

    if (
      thumbResolved &&
      urlResolved &&
      looksLikeVideoFileUrl(thumbResolved) &&
      (thumbResolved === urlResolved || looksLikeVideoFileUrl(urlResolved))
    ) {
      thumbResolved = null;
    }

    const thumbForCard =
      thumbResolved && looksLikeVideoFileUrl(thumbResolved) ? null : thumbResolved;

    const imageThumbRaw = pickPortraitThumbUrl(item, imageMain);
    const imageThumb = imageThumbRaw ? resolvePortraitImageUrl(imageThumbRaw) : null;

    const rawId = firstNonEmptyString(
      item.id,
      item.Id,
      item.asset_id,
      item.AssetId,
      item.assetId,
    );

    return {
      id: rawId,
      sourceAssetId: rawId,
      type: mediaType,
      name: String(item.name ?? item.Name ?? '未命名素材'),
      url: urlResolved,
      thumb: isVideo ? thumbForCard : imageThumb,
    } satisfies Asset;
  });
};
